import { signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Table } from 'primeng/table';
import { ConfirmationService } from 'primeng/api';
import { UserClient } from '../models/user-client.model';
import { UserClientService } from '../services/user-client.service';
import { MasterViewModel } from '../../../base-classes/master-view-model';
import { ToastService } from '../../../toast/toast.service';

export class UserClientMappingViewModel extends MasterViewModel<UserClient> {
    private router = inject(Router);
    private userClientService = inject(UserClientService);
    private confirmationService = inject(ConfirmationService);
    private toastService = inject(ToastService);

    // constructor() {
    //     super(undefined as any, undefined as any, undefined as any);
    // }

    // Data signals
    userClients = signal<any[]>([]);
    userList = signal<any[]>([]);
    clientList = signal<any[]>([]);
    selectedUserId = signal<string | null>(null);
    selectedClientId = signal<string | null>(null);

    // Dialog signals
    viewDialog = signal<boolean>(false);
    createDialog = signal<boolean>(false);
    dialogIsEdit = signal<boolean>(false);
    dlgAssignType = signal<'client' | 'user'>('client');
    dlgSelectedClientId = signal<string | null>(null);
    dlgSelectedUserId = signal<string | null>(null);
    dlgUserSelectionRows = signal<any[]>([]);
    dlgClientSelectionRows = signal<any[]>([]);

    // Loading signals
    isLoadingUserClients = signal<boolean>(false);
    isLoadingUsers = signal<boolean>(false);
    isLoadingClients = signal<boolean>(false);

    // Dialog state
    selectedUserClientForView: any = { userName: '', clientName: '' };
    private originalUserSelectionRows: any[] = [];
    private originalClientSelectionRows: any[] = [];
    private originalUserClientRecords: any[] = [];

    // Add a signal to track initial preselected items in create mode
    private initialUserSelectionRows: any[] = [];
    private initialClientSelectionRows: any[] = [];

    // Configuration
    assignTypeOptions = [
        { label: 'By Client', value: 'client' },
        { label: 'By User', value: 'user' }
    ];
    cols = [
        { field: 'userName', header: 'User' },
        { field: 'clientName', header: 'Client' }
    ];
    canManageModule = true;

    // Computed signals
    isDialogAssignTypeDisabled = computed(() => this.dialogIsEdit());
    isDialogUserDropdownDisabled = computed(() => this.dialogIsEdit() && this.dlgAssignType() === 'user');
    isDialogClientDropdownDisabled = computed(() => this.dialogIsEdit() && this.dlgAssignType() === 'client');
    dialogSaveButtonLabel = computed(() => this.dialogIsEdit() ? 'Update' : 'Submit');

    private hasSelectionsChanged = computed(() => {
        if (!this.dialogIsEdit()) return false;

        if (this.dlgAssignType() === 'client') {
            const currentCodes = this.dlgUserSelectionRows().map(u => u.code).sort();
            const originalCodes = this.originalUserSelectionRows.map(u => u.code).sort();
            return currentCodes.length !== originalCodes.length || currentCodes.some((code, i) => code !== originalCodes[i]);
        }

        const currentCodes = this.dlgClientSelectionRows().map(c => c.code).sort();
        const originalCodes = this.originalClientSelectionRows.map(c => c.code).sort();
        return currentCodes.length !== originalCodes.length || currentCodes.some((code, i) => code !== originalCodes[i]);
    });

    filteredUserClients = computed(() => {
        const all = this.userClients();
        if (!Array.isArray(all) || !all.length) return [];

        const userId = this.selectedUserId();
        const clientId = this.selectedClientId();

        // First, determine which users to include based on filters
        let usersToInclude = new Set<string>();

        if (userId || clientId) {
            all.forEach(uc => {
                if (!uc?.userId) return;

                const matchesUser = !userId || uc.userId === userId;
                const matchesClient = !clientId || uc.clientId === clientId;

                if (matchesUser && matchesClient) {
                    usersToInclude.add(uc.userId);
                }
            });
        } else {
            // No filters, include all users
            all.forEach(uc => {
                if (uc?.userId) usersToInclude.add(uc.userId);
            });
        }

        // Now build the result with ALL clients for each included user
        const byUser = new Map();
        all.forEach(uc => {
            if (!uc?.userId || !usersToInclude.has(uc.userId)) return;

            const existing = byUser.get(uc.userId);
            if (!existing) {
                byUser.set(uc.userId, {
                    userId: uc.userId,
                    userName: uc.userName || '',
                    clientIds: uc.clientId ? [uc.clientId] : [],
                    clientName: uc.clientName || ''
                });
            } else {
                if (uc.clientId && !existing.clientIds.includes(uc.clientId)) existing.clientIds.push(uc.clientId);
                const names = existing.clientName.split(',').map((n: string) => n.trim()).filter(Boolean);
                if (uc.clientName && !names.includes(uc.clientName)) names.push(uc.clientName);
                existing.clientName = names.join(', ');
            }
        });

        return Array.from(byUser.values());
    });

    public init(): void {
        this.getAllUserClient();
        this.loadUsers();
        this.loadClients();
    }

    public getAllUserClient(): void {
        this.userClientService.getUserClients().subscribe({
            next: (response: any) => {
                const list = Array.isArray(response) ? response : (response?.data || []);
                this.userClients.set(list.map((uc: any) => ({
                    ...uc,
                    userName: uc.user ? `${uc.user.firstName} ${uc.user.lastName}`.trim() : '',
                    clientName: uc.client?.name || ''
                })));
                this.isLoadingUserClients.set(false);
            },
            error: () => {
                this.userClients.set([]);
                this.isLoadingUserClients.set(false);
            }
        });
    }

    public loadUsers(): void {
        this.userClientService.getUserList().subscribe({
            next: (list: any) => {
                const data = list?.data || list;
                this.userList.set(data?.map((user: any) => ({
                    name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
                    code: user.id
                })) || []);
                this.isLoadingUsers.set(false);
            },
            error: () => {
                this.userList.set([]);
                this.isLoadingUsers.set(false);
            }
        });
    }

    public loadClients(): void {
        this.userClientService.getClientList().subscribe({
            next: (response: any) => {
                const list = response?.data || [];
                this.clientList.set(list.map((client: any) => ({ name: client.name, code: client.id })));
                this.isLoadingClients.set(false);
            },
            error: () => {
                this.clientList.set([]);
                this.isLoadingClients.set(false);
            }
        });
    }

    openNew(): void {
        this.resetDialog();
        this.createDialog.set(true);
    }

    onGlobalFilter(table: Table, event: Event): void {
        table.filterGlobal((event.target as HTMLInputElement).value, 'contains');
    }

    viewUserClient(userClient: any): void {
        this.selectedUserClientForView = { userName: userClient.userName, clientName: userClient.clientName };
        this.viewDialog.set(true);
    }

    hideViewDialog(): void {
        this.viewDialog.set(false);
        this.selectedUserClientForView = { userName: '', clientName: '' };
    }

    isDialogSaveDisabled(): boolean {
        const hasRequiredData = this.dlgAssignType() === 'client'
            ? this.dlgSelectedClientId() && this.dlgUserSelectionRows().length > 0
            : this.dlgSelectedUserId() && this.dlgClientSelectionRows().length > 0;

        return this.dialogIsEdit() ? !hasRequiredData || !this.hasSelectionsChanged() : !hasRequiredData;
    }

    editUserClient(userClient: any): void {
        this.dialogIsEdit.set(true);

        if (userClient.userId) {
            this.originalUserClientRecords = this.userClients().filter(uc => uc.userId === userClient.userId);
        }

        this.initDialogFromEdit(userClient);

        this.originalUserSelectionRows = [...this.dlgUserSelectionRows()];
        this.originalClientSelectionRows = [...this.dlgClientSelectionRows()];

        this.createDialog.set(true);
    }

    deleteUserClient(userClient: any): void {
        this.confirmationService.confirm({
            message: `Are you sure you want to delete all client mappings for user "${userClient.userName}"?`,
            header: 'Confirm Delete',
            acceptButtonStyleClass: 'p-button-success',
            rejectButtonStyleClass: 'p-button-warn',
            accept: () => {
                const relatedRecords = userClient.userId
                    ? this.userClients().filter(uc => uc.userId === userClient.userId)
                    : this.userClients().filter(uc => uc.id === userClient.id);

                const payload = relatedRecords.map(record => ({
                    id: record.id,
                    clientId: record.clientId,
                    clientName: record.clientName,
                    userId: record.userId,
                    isActive: false,
                    isModified: true
                }));

                if (payload.length === 0) return;

                this.userClientService.createUserClients(payload).subscribe({
                    next: () => {
                        this.toastService.success('User client mappings deleted successfully');
                        this.getAllUserClient();
                    },
                    error: (err: any) => {
                        this.toastService.error('Failed to delete user client mappings');
                    }
                });
            }
        });
    }

    addUserClient(newUserClient: any): void {
        const currentList = this.userClients();
        const newId = Math.max(...currentList.map(uc => uc.id || 0), 0) + 1;
        this.userClients.set([...currentList, { ...newUserClient, id: newId }]);
    }

    updateUserClient(updatedUserClient: any): void {
        const currentList = this.userClients();
        this.userClients.set(currentList.map(uc =>
            uc.id === updatedUserClient.id ? updatedUserClient : uc
        ));
    }

    onCreateDialogSave(payload: any): void {
        if (!payload) return;

        this.userClientService.createUserClients(payload).subscribe({
            next: () => {
                const message = this.dialogIsEdit()
                    ? 'User client mappings updated successfully'
                    : 'User client mappings created successfully';
                this.toastService.success(message);
                this.getAllUserClient();
                this.onCreateDialogClose();

            },
            error: (err: any) => {
                const message = this.dialogIsEdit()
                    ? 'Failed to update user client mappings'
                    : 'Failed to create user client mappings';
                this.toastService.error(message);
            }
        });
    }

    onCreateDialogClose(): void {
        this.createDialog.set(false);
        this.resetDialog();
    }

    onDialogAssignTypeChange(val: 'client' | 'user'): void {
        this.dlgAssignType.set(val);
        this.dlgSelectedClientId.set(null);
        this.dlgSelectedUserId.set(null);
        this.dlgUserSelectionRows.set([]);
        this.dlgClientSelectionRows.set([]);
    }

    onDialogClientChange(clientId: string | null): void {
        this.dlgSelectedClientId.set(clientId);
        this.dlgUserSelectionRows.set([]);
        if (clientId) {
            this.preselectUsersForClient(clientId);
            // Store initial selections for create mode
            if (!this.dialogIsEdit()) {
                this.initialUserSelectionRows = [...this.dlgUserSelectionRows()];
            }
        }
    }

    onDialogUserChange(userId: string | null): void {
        this.dlgSelectedUserId.set(userId);
        this.dlgClientSelectionRows.set([]);
        if (userId) {
            this.preselectClientsForUser(userId);
            // Store initial selections for create mode
            if (!this.dialogIsEdit()) {
                this.initialClientSelectionRows = [...this.dlgClientSelectionRows()];
            }
        }
    }

    buildDialogPayload(): any | null {
        if (this.dialogIsEdit()) return this.buildUpdatePayload();

        if (this.dlgAssignType() === 'client') {
            const clientId = this.dlgSelectedClientId();
            if (!clientId) return null;

            const client = this.clientList().find(c => c.code === clientId);
            const currentUserCodes = this.dlgUserSelectionRows().map(u => u.code);
            const initialUserCodes = this.initialUserSelectionRows.map(u => u.code);

            const payload: any[] = [];

            // Add selected users
            currentUserCodes.forEach(userCode => {
                const existing = this.userClients().find(
                    uc => uc.clientId === clientId && uc.userId === userCode
                );

                const wasInitiallySelected = initialUserCodes.includes(userCode);
                const isNewSelection = !wasInitiallySelected;
                const wasInactive = existing && !existing.isActive;

                payload.push({
                    ...(existing?.id && { id: existing.id }),
                    clientId,
                    clientName: client?.name || clientId,
                    userId: userCode,
                    isActive: true,
                    isModified: isNewSelection || wasInactive
                });
            });

            // Add unselected users that were initially selected (isActive: false)
            initialUserCodes.forEach(userCode => {
                if (!currentUserCodes.includes(userCode)) {
                    const existing = this.userClients().find(
                        uc => uc.clientId === clientId && uc.userId === userCode
                    );

                    if (existing?.id) {
                        payload.push({
                            id: existing.id,
                            clientId,
                            clientName: client?.name || clientId,
                            userId: userCode,
                            isActive: false,
                            isModified: true
                        });
                    }
                }
            });

            return payload.length > 0 ? payload : null;
        }

        const userId = this.dlgSelectedUserId();
        if (!userId) return null;

        const currentClientCodes = this.dlgClientSelectionRows().map(c => c.code);
        const initialClientCodes = this.initialClientSelectionRows.map(c => c.code);

        const payload: any[] = [];

        // Add selected clients
        currentClientCodes.forEach(clientCode => {
            const existing = this.userClients().find(
                uc => uc.userId === userId && uc.clientId === clientCode
            );
            const client = this.clientList().find(c => c.code === clientCode);

            const wasInitiallySelected = initialClientCodes.includes(clientCode);
            const isNewSelection = !wasInitiallySelected;
            const wasInactive = existing && !existing.isActive;

            payload.push({
                ...(existing?.id && { id: existing.id }),
                clientId: clientCode,
                clientName: client?.name || clientCode,
                userId,
                isActive: true,
                isModified: isNewSelection || wasInactive
            });
        });

        // Add unselected clients that were initially selected (isActive: false)
        initialClientCodes.forEach(clientCode => {
            if (!currentClientCodes.includes(clientCode)) {
                const existing = this.userClients().find(
                    uc => uc.userId === userId && uc.clientId === clientCode
                );

                if (existing?.id) {
                    const client = this.clientList().find(c => c.code === clientCode);
                    payload.push({
                        id: existing.id,
                        clientId: clientCode,
                        clientName: client?.name || clientCode,
                        userId,
                        isActive: false,
                        isModified: true
                    });
                }
            }
        });

        return payload.length > 0 ? payload : null;
    }

    private buildUpdatePayload(): any | null {
        if (this.dlgAssignType() === 'client') {
            const clientId = this.dlgSelectedClientId();
            if (!clientId) return null;

            const client = this.clientList().find(c => c.code === clientId);
            const currentUserCodes = this.dlgUserSelectionRows().map(u => u.code);
            const originalUserCodes = this.originalUserSelectionRows.map(u => u.code);

            const payload: any[] = [];

            // Add selected users
            currentUserCodes.forEach(userCode => {
                const existing = this.originalUserClientRecords.find(
                    rec => rec.clientId === clientId && rec.userId === userCode
                );

                const wasOriginallySelected = originalUserCodes.includes(userCode);
                const isNewSelection = !wasOriginallySelected;
                const wasInactive = existing && !existing.isActive;

                payload.push({
                    ...(existing?.id && { id: existing.id }),
                    clientId,
                    clientName: client?.name || clientId,
                    userId: userCode,
                    isActive: true,
                    isModified: isNewSelection || wasInactive
                });
            });

            // Add unselected users that were originally selected (isActive: false)
            originalUserCodes.forEach(userCode => {
                if (!currentUserCodes.includes(userCode)) {
                    const existing = this.originalUserClientRecords.find(
                        rec => rec.clientId === clientId && rec.userId === userCode
                    );
                    if (existing?.id) {
                        payload.push({
                            id: existing.id,
                            clientId,
                            clientName: client?.name || clientId,
                            userId: userCode,
                            isActive: false,
                            isModified: true
                        });
                    }
                }
            });

            return payload.length > 0 ? payload : null;
        }

        const userId = this.dlgSelectedUserId();
        if (!userId) return null;

        const currentClientCodes = this.dlgClientSelectionRows().map(c => c.code);
        const originalClientCodes = this.originalClientSelectionRows.map(c => c.code);

        const payload: any[] = [];

        // Add selected clients
        currentClientCodes.forEach(clientCode => {
            const existing = this.originalUserClientRecords.find(
                rec => rec.userId === userId && rec.clientId === clientCode
            );
            const client = this.clientList().find(c => c.code === clientCode);

            const wasOriginallySelected = originalClientCodes.includes(clientCode);
            const isNewSelection = !wasOriginallySelected;
            const wasInactive = existing && !existing.isActive;

            payload.push({
                ...(existing?.id && { id: existing.id }),
                clientId: clientCode,
                clientName: client?.name || clientCode,
                userId,
                isActive: true,
                isModified: isNewSelection || wasInactive
            });
        });

        // Add unselected clients that were originally selected (isActive: false)
        originalClientCodes.forEach(clientCode => {
            if (!currentClientCodes.includes(clientCode)) {
                const existing = this.originalUserClientRecords.find(
                    rec => rec.userId === userId && rec.clientId === clientCode
                );
                if (existing?.id) {
                    const client = this.clientList().find(c => c.code === clientCode);
                    payload.push({
                        id: existing.id,
                        clientId: clientCode,
                        clientName: client?.name || clientCode,
                        userId,
                        isActive: false,
                        isModified: true
                    });
                }
            }
        });

        return payload.length > 0 ? payload : null;
    }

    private resetDialog(): void {
        this.dialogIsEdit.set(false);
        this.dlgAssignType.set('client');
        this.dlgSelectedClientId.set(null);
        this.dlgSelectedUserId.set(null);
        this.dlgUserSelectionRows.set([]);
        this.dlgClientSelectionRows.set([]);
        this.originalUserSelectionRows = [];
        this.originalClientSelectionRows = [];
        this.originalUserClientRecords = [];
        this.initialUserSelectionRows = [];
        this.initialClientSelectionRows = [];
    }

    private preselectUsersForClient(clientId: string): void {
        const userIds = this.userClients().filter(uc => uc?.clientId === clientId && uc.userId).map(uc => uc.userId);
        this.dlgUserSelectionRows.set(this.userList().filter(u => userIds.includes(u.code)));
    }

    private preselectClientsForUser(userId: string): void {
        const clientIds = this.userClients().filter(uc => uc?.userId === userId && uc.clientId).map(uc => uc.clientId);
        this.dlgClientSelectionRows.set(this.clientList().filter(c => clientIds.includes(c.code)));
    }

    private initDialogFromEdit(userClient: any): void {
        if (userClient?.userId && userClient?.clientIds?.length) {
            this.dlgAssignType.set('user');
            this.dlgSelectedUserId.set(userClient.userId);
            this.dlgClientSelectionRows.set(this.clientList().filter(c => userClient.clientIds.includes(c.code)));
        }
    }
}
